import axios from "axios";
import { setQueueState } from "@/lib/queueGate";
import { setSubscriptionBlock, blockFromError } from "@/lib/subscriptionGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "lumiere_token";

// "Se souvenir de moi" persiste le token en localStorage (survit à la
// fermeture du navigateur, jusqu'à l'expiration serveur du token) ; sans
// cette option, il vit en sessionStorage (effacé à la fermeture de
// l'onglet/navigateur) et le serveur lui donne de toute façon une durée
// de vie beaucoup plus courte. Les deux emplacements ne sont jamais
// utilisés en même temps pour éviter tout token orphelin obsolète.
export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token, remember) {
  if (remember) {
    localStorage.setItem(TOKEN_KEY, token);
    sessionStorage.removeItem(TOKEN_KEY);
  } else {
    sessionStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

// Boutique actuellement gérée par un superadmin (opérateur multi-boutiques,
// sans shop_id propre) — voir ShopSwitcher.jsx dans AdminLayout. Répercutée
// sur chaque requête via l'en-tête X-Shop-Id, validé côté backend
// (services/admin_service.get_admin_user) avant d'être utilisé à la place
// de la boutique par défaut. Sans effet pour un admin normal (shop_id fixe
// en base, l'en-tête est ignoré côté serveur pour ce rôle).
const SHOP_KEY = "lumiere_active_shop_id";

export function getActiveShopId() {
  return localStorage.getItem(SHOP_KEY) || "";
}

// Identifiant anonyme de "visiteur" pour la salle d'attente publique (voir
// lib/queueGate.js) — envoyé sur CHAQUE requête via l'en-tête X-Queue-Session.
// C'est cet en-tête, et lui seul, qui fait compter un appel comme un
// visiteur côté serveur (voir storefront_queue_middleware) : au premier
// chargement d'une page, plusieurs appels partent en parallèle (theme/init,
// theme/sections, produits...) et doivent tous compter pour UN visiteur ;
// un appel sans cet en-tête (serveur Node du frontend, robots, monitoring)
// n'est pas un visiteur et ne consomme aucun slot. Généré une seule fois et
// persisté en localStorage (survit aux rechargements, y compris pendant une
// attente en salle d'attente).
const QUEUE_SESSION_KEY = "ce_qsid";

function getOrCreateQueueSessionId() {
  let id = localStorage.getItem(QUEUE_SESSION_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(QUEUE_SESSION_KEY, id);
  }
  return id;
}

const queueSessionId = getOrCreateQueueSessionId();

export function setActiveShopId(shopId) {
  if (shopId) localStorage.setItem(SHOP_KEY, shopId);
  else localStorage.removeItem(SHOP_KEY);
}

// withCredentials : nécessaire pour que le cookie anonyme de la salle
// d'attente (voir lib/queueGate.js) circule même quand front et back sont
// sur des origines différentes (dev local, ou sous-domaines séparés en prod).
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const shopId = getActiveShopId();
  if (shopId) config.headers["X-Shop-Id"] = shopId;
  config.headers["X-Queue-Session"] = queueSessionId;
  return config;
});

// Un 503 { queued: true, position, waiting } signale que le back-end a mis
// cette requête en salle d'attente (trafic public au-delà de la capacité
// configurée pour la boutique — voir storefront_queue_middleware côté
// serveur). On propage l'info au store global plutôt que de la traiter ici :
// c'est App.js qui bascule tout l'arbre applicatif sur <WaitingRoom/>.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error.response?.data;
    if (error.response?.status === 503 && data?.queued) {
      setQueueState({ position: data.position, waiting: data.waiting, shopName: data.shop_name });
    }
    // 402 { code: "subscription_blocked" } : essai terminé, abonnement échu ou
    // blocage manuel — App.js bascule sur la page de blocage (voir
    // lib/subscriptionGate.js et components/SubscriptionBlocked.jsx).
    const blocked = blockFromError(error);
    if (blocked) setSubscriptionBlock(blocked);
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const eur = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);

export default api;
